import { useCallback, useRef } from "react";
import { useInvalidateMCPConnections } from "../hooks/useMCPQueryScope";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Spinner } from "../../../components/ui/spinner";
import AccessLevelSection from "./AccessLevelSection";
import DetailHeader from "./DetailHeader";
import NeedsAttentionBanner from "./NeedsAttentionBanner";
import PermissionsSection from "./PermissionsSection";
import {
  openOAuthPopup,
  settleOAuthPopup,
  watchOAuthPopupCompletion,
} from "../oauth";
import { useMCPConnection } from "../hooks/useMCPConnection";
import { useMCPConnectionTest } from "../hooks/useMCPConnectionTest";
import { useMCPTools } from "../hooks/useMCPTools";
import { useReconnectMCPConnection } from "../hooks/useReconnectMCPConnection";
import { useUpdateMCPConnection } from "../hooks/useUpdateMCPConnection";
import { getMCPStatusState } from "../status";
import { canManageMcpConnection } from "../connectionAccess";
import type { OrgUser } from "../hooks/useUsers";

type Props = {
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
  connectionId: string;
  currentOrgUser: OrgUser | undefined;
};

/**
 * Detail for one connection, rendered beside the list rather than over it.
 *
 * This was a MUI Drawer. As a panel it sits in the page's own flow, so it is the
 * page that decides where it goes — see the split in index.tsx. Two things came
 * with the Drawer that had to be replaced by hand:
 *
 *  - Esc and click-away closing. Gone; DetailHeader's × is now the only way out.
 *  - A full-height sheet with its own surface. The pane is flat now: no card, no
 *    border, no rounding. The only line between it and the list is the divider
 *    the page draws, and the pane's height comes from the split, not from here.
 *
 * There is no `open` prop any more: the page mounts this only for a selected row,
 * so "open" and "mounted" are the same thing. That also means the detail queries
 * unmount on close rather than idling in the background, which is what the old
 * `open ? id : null` guards were working around.
 */
const MCPDetailPanel = ({
  onClose,
  onEdit,
  onDelete,
  connectionId,
  currentOrgUser,
}: Props) => {
  const { t } = useTranslation();
  const invalidateConnections = useInvalidateMCPConnections();
  const { connection, dto, hasAuthoritativeData, isLoading, error } =
    useMCPConnection(connectionId);
  const {
    status: liveStatus,
    errorMessage: testError,
    authRequired,
    isTesting,
    refetch: rerunTest,
  } = useMCPConnectionTest(connectionId);
  const {
    tools,
    isLoading: toolsLoading,
    error: toolsError,
  } = useMCPTools(connectionId);
  const updateMutation = useUpdateMCPConnection();
  const reconnectMutation = useReconnectMCPConnection();
  const activeMutationInFlight = useRef(false);

  const isActive = dto?.is_active ?? true;
  const canManage =
    hasAuthoritativeData &&
    canManageMcpConnection(dto?.sharing, currentOrgUser);
  const statusState = getMCPStatusState({
    isActive,
    needsAttention: connection?.needsAttention ?? false,
    authRequired,
    liveStatus,
  });

  const handleToggleActive = useCallback(() => {
    if (
      !canManage ||
      updateMutation.isPending ||
      activeMutationInFlight.current
    ) {
      return;
    }
    activeMutationInFlight.current = true;
    updateMutation.mutate(
      {
        id: connectionId,
        payload: { is_active: !isActive },
      },
      {
        onSettled: () => {
          activeMutationInFlight.current = false;
        },
      },
    );
  }, [canManage, connectionId, isActive, updateMutation]);

  const handleRefresh = useCallback(() => {
    rerunTest();
  }, [rerunTest]);

  const handleReconnect = useCallback(() => {
    if (!canManage || reconnectMutation.isPending) return;
    // Open the tab synchronously on click so the popup blocker allows it.
    const popup = openOAuthPopup();
    reconnectMutation.mutate(connectionId, {
      onResourceSuccess: (init, handoff) => {
        if (popup?.closed) return;
        const { redirected, blocked } = settleOAuthPopup(
          popup,
          init.authorization_url,
        );
        if (blocked && init.authorization_url) {
          toast.warning(
            t("mcpPage.toasts.popupBlocked", { url: init.authorization_url }),
          );
        } else if (redirected) {
          handoff();
          watchOAuthPopupCompletion(popup!, init.authorization_url, () => {
            void invalidateConnections();
          });
          toast.success(t("mcpPage.toasts.completeAuthorization"));
        }
      },
      // Close only this operation's pre-opened window, even after scope disposal.
      onCleanup: () => { if (popup && !popup.closed) popup.close(); },
    });
  }, [canManage, connectionId, invalidateConnections, reconnectMutation, t]);

  return (
    <aside>
      {isLoading && !connection ? (
        <div className="flex items-center justify-center py-12">
          <Spinner className="size-5 text-muted-foreground" />
        </div>
      ) : error && !connection ? (
        <div className="px-6 py-12 text-center text-sm text-destructive">
          {t("mcpPage.errors.loadDetails")}
        </div>
      ) : connection ? (
        <div className="flex flex-col">
          <DetailHeader
            connection={connection}
            status={statusState.status}
            errorMessage={testError}
            isActive={isActive}
            canManage={canManage}
            isTogglingActive={updateMutation.isPending}
            onToggleActive={handleToggleActive}
            onRefresh={handleRefresh}
            isRefreshing={isTesting}
            onEdit={onEdit}
            onDelete={onDelete}
            onClose={onClose}
          />

          {statusState.showReauthentication && (
            <NeedsAttentionBanner
              message={testError}
              isReconnecting={reconnectMutation.isPending}
              canReconnect={canManage}
              onReconnect={handleReconnect}
            />
          )}

          <div className="border-b border-border p-2">
            <p className="min-h-[79px] p-2 text-sm leading-[1.5] text-foreground">
              {connection.description}
            </p>
          </div>

          <PermissionsSection
            connectionId={connection.id}
            tools={tools}
            isLoading={toolsLoading}
            errorMessage={toolsError ? t("mcpPage.errors.loadTools") : null}
            canManage={canManage}
          />
          <AccessLevelSection
            connectionId={connection.id}
            sharing={dto?.sharing}
            canManage={canManage}
          />
        </div>
      ) : null}
    </aside>
  );
};

export default MCPDetailPanel;
